import { Dialog, DialogPanel } from "@tremor/react";
import {
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Layers,
  PauseCircle,
  Plus,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  useGroup,
  useUpdateGroup,
  useUpdateGroupMembers,
} from "../queries/groups.js";
import { useMonitors } from "../queries/monitors.js";
import DeleteGroupDialog from "../components/DeleteGroupDialog.jsx";

import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_DOT = {
  up: "bg-green-500",
  degraded: "bg-yellow-400",
  down: "bg-red-500",
  paused: "bg-gray-400",
};

const STATUS_BADGE = {
  up: "bg-green-100 text-green-700",
  degraded: "bg-yellow-100 text-yellow-700",
  down: "bg-red-100 text-red-700",
  paused: "bg-gray-100 text-gray-500",
};

const STATUS_ICON = {
  up: <CheckCircle className="h-4 w-4 text-green-500" />,
  degraded: <Zap className="h-4 w-4 text-yellow-500" />,
  down: <XCircle className="h-4 w-4 text-red-500" />,
  paused: <PauseCircle className="h-4 w-4 text-gray-400" />,
};

function fmtMs(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function fmtTime(iso, i18n) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" });
}

// ---------------------------------------------------------------------------
// Add Monitors modal
// ---------------------------------------------------------------------------

function AddMonitorsModal({ groupId, existingIds, onClose, onAdded }) {
  const { t } = useTranslation("group_detail");
  const [selected, setSelected] = useState(new Set());

  const monitorsQuery = useMonitors();
  const updateMembers = useUpdateGroupMembers();
  const loading = monitorsQuery.isPending;
  const saving = updateMembers.isPending;
  const monitors = (monitorsQuery.data?.data ?? []).filter(
    (m) => !existingIds.has(m.id)
  );

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleAdd() {
    if (selected.size === 0) return;
    try {
      const newIds = [...existingIds, ...selected];
      await updateMembers.mutateAsync({ id: groupId, monitorIds: newIds });
      toast.success(t("modal.success_add", { count: selected.size }));
      onAdded();
      onClose();
    } catch {
      toast.error(t("modal.error_add") || "Failed to add monitors");
    }
  }

  return (
    <Dialog open onClose={onClose} static>
      <DialogPanel className="mx-auto my-12 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-sm font-semibold text-gray-900">{t("modal.title")}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto px-5 py-4">
          {loading && <p className="text-sm text-gray-400">{t("modal.loading")}</p>}
          {!loading && monitors.length === 0 && (
            <p className="text-sm text-gray-400">
              {t("modal.all_in")}
            </p>
          )}
          <div className="space-y-1">
            {monitors.map((m) => (
              <label
                key={m.id}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selected.has(m.id)}
                  onChange={() => toggle(m.id)}
                  className="h-4 w-4 rounded border-gray-300 text-teal-600"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-800">{m.name}</p>
                  <p className="truncate font-mono text-xs text-gray-400">
                    {m.type} · {m.target}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            {t("modal.cancel")}
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving || selected.size === 0}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-40"
          >
            {saving
              ? t("common:common.saving")
              : t("modal.add_button", { count: selected.size > 0 ? selected.size : "" })}
          </button>
        </div>
      </DialogPanel>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Member row
// ---------------------------------------------------------------------------

function MemberRow({ member, onRemove }) {
  const { t, i18n } = useTranslation("group_detail");
  const [removing, setRemoving] = useState(false);

  async function handleRemove() {
    setRemoving(true);
    try {
      await onRemove(member.monitor_id);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <tr className="group border-t border-gray-100 hover:bg-gray-50">
      <td className="py-3 pl-4 pr-2">
        <div className="flex items-center gap-2">
          {STATUS_ICON[member.status] ?? STATUS_ICON.up}
          <Link
            to={`/monitors/${member.monitor_id}`}
            className="text-sm font-medium text-gray-900 hover:text-teal-600"
          >
            {member.name}
          </Link>
        </div>
      </td>
      <td className="px-2 py-3">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
          {member.type}
        </span>
      </td>
      <td className="px-2 py-3">
        <span className="font-mono text-xs text-gray-500 truncate max-w-[160px] block">
          {member.target}
        </span>
      </td>
      <td className="px-2 py-3 text-right text-xs text-gray-500">
        {fmtMs(member.last_response_time_ms)}
      </td>
      <td className="px-2 py-3 text-right text-xs text-gray-500">
        {fmtTime(member.last_check_at, i18n)}
      </td>
      <td className="py-3 pl-2 pr-4 text-right">
        <button
          type="button"
          onClick={handleRemove}
          disabled={removing}
          className="rounded p-1 text-gray-300 opacity-0 transition hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
          title={t("table.remove_title")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Group Detail page
// ---------------------------------------------------------------------------

export default function GroupDetail() {
  const { t } = useTranslation(["group_detail", "common"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const groupQuery = useGroup(id);
  const updateMutation = useUpdateGroup();
  const updateMembers = useUpdateGroupMembers();
  const group = groupQuery.data;
  const loading = groupQuery.isPending;
  const saving = updateMutation.isPending;

  // Sync the local edit fields whenever the cached group changes.
  useEffect(() => {
    if (group) {
      setEditName(group.name);
      setEditDesc(group.description ?? "");
    }
  }, [group?.id, group?.name, group?.description]);

  useEffect(() => {
    if (groupQuery.isError) toast.error(t("error_load"));
  }, [groupQuery.isError, t]);

  async function handleSaveName(e) {
    e.preventDefault();
    try {
      await updateMutation.mutateAsync({
        id,
        data: { name: editName, description: editDesc || undefined },
      });
      setEditing(false);
      toast.success(t("header.success_save"));
    } catch {
      toast.error(t("header.error_save"));
    }
  }

  async function handleRemoveMember(monitorId) {
    const remaining = group.members
      .filter((m) => m.monitor_id !== monitorId)
      .map((m) => m.monitor_id);
    try {
      await updateMembers.mutateAsync({ id, monitorIds: remaining });
      toast.success(t("table.success_remove"));
    } catch {
      toast.error(t("table.error_remove"));
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-gray-400">{t("loading")}</p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-red-500">{t("not_found")}</p>
        <Link to="/groups" className="mt-4 text-sm text-teal-600 hover:underline">
          ← {t("back_link")}
        </Link>
      </div>
    );
  }

  const existingIds = new Set(group.members.map((m) => m.monitor_id));

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      {/* Back nav */}
      <Link
        to="/groups"
        className="mb-6 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("back_link")}
      </Link>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {editing ? (
            <form onSubmit={handleSaveName} className="space-y-2">
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xl font-bold text-gray-900 outline-none focus:border-teal-600"
              />
              <input
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                placeholder={t("header.edit_description")}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 outline-none focus:border-teal-600"
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-40"
                >
                  {saving ? t("header.saving") : t("common:common.save")}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  {t("common:common.cancel")}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span
                  className={`h-3 w-3 flex-shrink-0 rounded-full ${STATUS_DOT[group.status] ?? STATUS_DOT.up}`}
                />
                <h1
                  className="text-2xl font-bold text-gray-900 cursor-pointer hover:text-teal-600"
                  onClick={() => setEditing(true)}
                  title={t("header.edit_title")}
                >
                  {group.name}
                </h1>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[group.status] ?? STATUS_BADGE.up}`}
                >
                  {group.status === "up" ? t("header.operational") : group.status}
                </span>
              </div>
              {group.description && (
                <p className="mt-1 text-sm text-gray-500">{group.description}</p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5">
            <Layers className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">
              {t("header.monitors_count", { count: group.member_count })}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            {t("header.add_monitor")}
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteDialog(true)}
            className="flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
            title="Delete group"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Members table */}
      {group.members.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-16 text-center">
          <Layers className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-sm font-medium text-gray-600">
            {t("empty.title")}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {t("empty.description")}
          </p>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="mt-4 flex items-center gap-1.5 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          >
            <Plus className="h-4 w-4" />
            {t("header.add_monitor")}
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th className="py-3 pl-4 pr-2">{t("table.monitor")}</th>
                <th className="px-2 py-3">{t("table.type")}</th>
                <th className="px-2 py-3">{t("table.target")}</th>
                <th className="px-2 py-3 text-right">{t("table.response")}</th>
                <th className="px-2 py-3 text-right">{t("table.last_checked")}</th>
                <th className="py-3 pl-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {group.members.map((member) => (
                <MemberRow
                  key={member.monitor_id}
                  member={member}
                  onRemove={handleRemoveMember}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add monitors modal */}
      {showAddModal && (
        <AddMonitorsModal
          groupId={id}
          existingIds={existingIds}
          onClose={() => setShowAddModal(false)}
          onAdded={() => groupQuery.refetch()}
        />
      )}

      {showDeleteDialog && (
        <DeleteGroupDialog
          group={group}
          onClose={() => setShowDeleteDialog(false)}
          onDeleted={() => navigate("/groups")}
        />
      )}
    </div>
  );
}
